import { SendOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../shared/functions/formatDate";
import { handleError } from "../../../shared/functions/handleError";
import {
  IVersion,
  IVersionNotification,
} from "../../../shared/interfaces/version/IVersion";
import { VersionService } from "../service";
import VersionNotifyModal from "./VersionNotifyModal";

interface IProps {
  versionId: number;
}

const VersionNotifications = ({ versionId }: IProps) => {
  const { t } = useTranslation();
  const [version, setVersion] = useState<IVersion | undefined>(undefined);
  const [notifications, setNotifications] = useState<IVersionNotification[]>(
    [],
  );
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => {
    VersionService.getNotifications(versionId)
      .then(setNotifications)
      .catch(handleError);
  }, [versionId]);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = () => {
    // Fetch the saved version so the dialog shows what will actually be sent
    VersionService.getSingle(versionId)
      .then((response) => {
        setVersion(response);
        setShowModal(true);
      })
      .catch(handleError);
  };

  return (
    <Card sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {t("version.notifications")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<SendOutlined />}
          onClick={openModal}
        >
          {t("version.notifyClients")}
        </Button>
      </Box>

      {notifications.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("version.noNotifications")}
        </Typography>
      ) : (
        <List dense disablePadding>
          {notifications.map((notification, index) => (
            <React.Fragment key={notification.id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                disableGutters
                secondaryAction={
                  <Chip
                    size="small"
                    color={notification.status === "sent" ? "success" : "error"}
                    label={t(
                      notification.status === "sent"
                        ? "version.statusSent"
                        : "version.statusFailed",
                    )}
                  />
                }
              >
                <ListItemText
                  primary={`${notification.clientName ?? "-"} · ${formatDate(
                    notification.createdAt,
                    "DD.MM.YYYY HH:mm",
                  )} · ${notification.language.toUpperCase()}`}
                  secondary={[
                    notification.recipients.join(", "),
                    notification.error,
                  ]
                    .filter(Boolean)
                    .join(" - ")}
                  sx={{ pr: 12 }}
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      <VersionNotifyModal
        version={version}
        open={showModal}
        onClose={() => setShowModal(false)}
        onSent={load}
      />
    </Card>
  );
};

export default VersionNotifications;
